import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertService, AlertType } from 'src/app/shared/services/alert.service';
import { MedicalRecordLookupService } from 'src/app/shared/services/API/medical-record/lookup.service';
import { PrescriptionSchedulingService } from 'src/app/shared/services/API/orchestrator-patient/prescription-scheduling.service';
import { VerifyPermissionService } from 'src/app/shared/services/API/user/verify-permission.service';
import { UtilService } from 'src/app/shared/services/util.service';
import { CheckRestrictedOverdueMedicationModalComponent } from '../../../check-restricted-overdue-medication-modal/check-restricted-overdue-medication-modal.component';
import { UserRoleEnum } from 'src/app/shared/enum/user-role.enum';
import { AllergyModel } from 'src/app/shared/services/models/medical-record/allergy.model';
import { GetListMedicineStruct } from 'src/app/shared/services/structs/pharmacy/get-list-medicine.struct';
import { VerifyPermissionResponse } from 'src/app/shared/services/responses/user/verify-permission.response';
import { StatusSchedulingEnum } from 'src/app/shared/enum/medical-record/status-scheduling.enum';
import { PrescriptionSolutionStruct } from 'src/app/shared/services/structs/pharmacy/prescription-solution.struct';
import { PrescriptionSolutionCheckStruct } from 'src/app/shared/services/structs/pharmacy/prescription-solution-check.struct';
import { PrescriptionModalComponent } from 'src/app/observation/pages/bed-management-details/presciption-list/prescription-modal/prescription-modal.component';
import { DilutionUnit } from 'src/app/shared/services/models/pharmacy/dilution-unit.model';
import { Dilution } from 'src/app/shared/services/models/pharmacy/dilution.model';
import { DoseToAdministered } from 'src/app/shared/services/models/pharmacy/dose-to-administered.model';
import { Frequency } from 'src/app/shared/services/models/pharmacy/frequency.model';
import { MeasurementUnit } from 'src/app/shared/services/models/pharmacy/measurement-unit.model';
import { MedicineAdministrationTypeModel } from 'src/app/shared/services/models/pharmacy/medicine-administration-type.model';
import { MedicinePresentationTypeModel } from 'src/app/shared/services/models/pharmacy/medicine-presentation-type.model';
import { SubFrequency } from 'src/app/shared/services/models/pharmacy/sub-frequency.model';
import { TreatmentPeriod } from 'src/app/shared/services/models/pharmacy/treatment-period.model';
import { Usage } from 'src/app/shared/services/models/pharmacy/usage.model';

@Component({
    selector: 'app-late-pending-solution-checks',
    templateUrl: './late-pending-solution-checks.component.html',
    styleUrls: ['./late-pending-solution-checks.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LatePendingSolutionChecksComponent implements OnInit {
  @Input() prescriptionSolutionStruct: PrescriptionSolutionStruct;
  @Input() patientName: string;
  @Input() birthDate: Date;
  @Input() idEpisode: number;
  @Input() allergiesToSave: AllergyModel[] = [];
  @Input() listMedicine: GetListMedicineStruct;
  @Input() listSolutionCheck: PrescriptionSolutionCheckStruct[] = [];
  @Input() listMedicinePresentationType: MedicinePresentationTypeModel[] = [];
  @Input() listDilution: Dilution[] = [];
  @Input() listMeasurementUnit: MeasurementUnit[] = [];
  @Input() listMedicineAdministrationType: MedicineAdministrationTypeModel[] = [];
  @Input() listDoseToAdministered: DoseToAdministered[] = [];
  @Input() listDilutionUnit: DilutionUnit[] = [];
  @Input() listFrequency: Frequency[] = [];
  @Input() listSubFrequency: SubFrequency[] = [];
  @Input() listTreatmentPeriod: TreatmentPeriod[] = [];
  @Input() listUsage: Usage[] = [];
  
  public statusSchedulingEnum: typeof StatusSchedulingEnum = StatusSchedulingEnum;

  constructor(
    public dialog: MatDialog,
    private alertService: AlertService,
    public prescriptionSchedulingService: PrescriptionSchedulingService,
    public medicalRecordLookupService: MedicalRecordLookupService,
    public verifyPermissionService: VerifyPermissionService,
    private utilService: UtilService
  ) { }

  ngOnInit(): void { }

  async onClickInPrescriptionCheck(solutionCheckStruct: PrescriptionSolutionCheckStruct): Promise<void> {
    let verifyPermissionResponse: VerifyPermissionResponse = null;

    let isToVerifyPermission = (solutionCheckStruct.prescriptionSolutionCheck.idStatusScheduling !== StatusSchedulingEnum.Suspenso
      && solutionCheckStruct.prescriptionSolutionCheck.idStatusScheduling !== StatusSchedulingEnum.Concluido);

    if (isToVerifyPermission) {
      verifyPermissionResponse = await this.checkRestrictedOverdueMedication();
      if (!verifyPermissionResponse?.havePermission)
        return;
    }

    this.openCheckPrescriptionModal(solutionCheckStruct, verifyPermissionResponse);
  }

  async checkRestrictedOverdueMedication(): Promise<VerifyPermissionResponse> {
    return new Promise((resolve) => {
      if (!this.hasRoleToCheckOverduePrescription()) {
        this.alertService.show('Erro', 'O usuário não possui permissão para liberar a checagem com restrição.', AlertType.error);
        resolve(null);
        return;
      }

      const dialogRef = this.dialog.open(CheckRestrictedOverdueMedicationModalComponent);

      dialogRef.afterClosed().subscribe(async (result) => {
        const hasPopulatedResult = result?.confirm && result?.response;

        if (!hasPopulatedResult) {
          resolve(null);
          return;
        }

        resolve(result?.response);
      });
    });
  }

  hasRoleToCheckOverduePrescription(): boolean {
    const listRole = this.utilService.getRoles();
    const isMaster = this.utilService.getToken().isMaster;

    return (listRole.some((x) => x.idRole === UserRoleEnum.checar_atrasado_com_restrição) || isMaster);
  }

  openCheckPrescriptionModal(solutionCheckStruct: PrescriptionSolutionCheckStruct, verifyPermissionResponse: VerifyPermissionResponse) {
    const dialogRef = this.dialog.open(PrescriptionModalComponent, {
      data: {
        prescription: {
          ...this.prescriptionSolutionStruct,
          tempCheck: solutionCheckStruct.prescriptionSolutionCheck.idStatusScheduling === StatusSchedulingEnum.Suspenso || solutionCheckStruct.prescriptionSolutionCheck.idStatusScheduling === StatusSchedulingEnum.Concluido
        },
        selectedPrescriptionCheck: solutionCheckStruct,
        patientName: this.patientName,
        birthDate: this.birthDate,
        actions: true,
        idEpisode: this.idEpisode,
        isMedicatedSolution: true,
        allergiesToSave: this.allergiesToSave,
        listMedicine: this.listMedicine,
        useScheduling: true,
        verifyPermissionResponse: verifyPermissionResponse,
        listPresentation: this.listMedicinePresentationType,
        listDilution: this.listDilution,
        listMeasurementUnit: this.listMeasurementUnit,
        listAdministrationType: this.listMedicineAdministrationType,
        listDoseToAdministered: this.listDoseToAdministered,
        listDilutionUnit: this.listDilutionUnit,
        listFrequency: this.listFrequency,
        listSubFrequency: this.listSubFrequency,
        listTreatmentPeriod: this.listTreatmentPeriod,
        listUsage: this.listUsage,
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.prescriptionSolutionCheckStruct && result?.prescriptionSolutionCheckStruct instanceof PrescriptionSolutionCheckStruct) {
        this.updateCheckedMedication(result.prescriptionSolutionCheckStruct);
      }
    });
  }

  updateCheckedMedication(solutionCheckStruct: PrescriptionSolutionCheckStruct): void {
    const indexCheck = this.listSolutionCheck.findIndex(l => l.prescriptionSolutionCheck.idPrescriptionSolutionCheck === solutionCheckStruct.prescriptionSolutionCheck.idPrescriptionSolutionCheck);
    const indexPrescriptionCheck = this.prescriptionSolutionStruct.listSolutionCheck.findIndex(x => x.prescriptionSolutionCheck.idPrescriptionSolutionCheck === solutionCheckStruct.prescriptionSolutionCheck.idPrescriptionSolutionCheck);

    if (indexPrescriptionCheck >= 0)
      this.prescriptionSolutionStruct.listSolutionCheck[indexPrescriptionCheck] = solutionCheckStruct;
    if (indexCheck >= 0) {
      this.listSolutionCheck[indexCheck] = solutionCheckStruct;
    } else {
      this.listSolutionCheck.push(solutionCheckStruct);
    }
  }
}
